// features/relatorios/components/OverviewSection.tsx -- Secao Visao Geral (F23-S06).
// Consome GET /api/reports/overview via useReportsOverview (TanStack Query).
// KPIs: leads, simulacoes, contratos, conversas. Reutiliza Stat (DS s9.8).
//
// Correcoes do diagnostico de dashboard/relatorios:
//   D1 -- "Total de leads" era all-time com o rotulo do periodo colado embaixo
//         ("TOTAL DE LEADS 4.080 -- Hoje"). Agora o card e do periodo e a base
//         historica aparece como snapshot, dita como tal.
//   D3 -- serie diaria, que nunca existiu apesar de a MV ja ser diaria.

import type { CommonReportQuery, OverviewResponse } from '@elemento/shared-schemas';
import * as React from 'react';

import { FreshnessIndicator } from '../../../components/ui/FreshnessIndicator';
import { Stat } from '../../../components/ui/Stat';
import { DailySeriesChart } from '../../dashboard/components/DailySeriesChart';
import { conversionDisplay } from '../../dashboard/utils/metrics';
import { useReportsOverview } from '../hooks/useReportsOverview';
import { fmtPercentFromRatio } from '../utils/format';

function fmtNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}
function fmtCurrency(n: number): string {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}
/**
 * Calcula o trend de delta entre dois periodos.
 * Retorna undefined quando nao ha dado anterior (esconde o pill).
 * Para uso com exactOptionalPropertyTypes: usar o helper trendProps().
 */
function deltaTrend(
  current: number,
  previous: number,
): { value: string; direction: 'up' | 'down' | 'neutral' } | undefined {
  if (previous === 0) return undefined;
  const delta = (current - previous) / previous;
  const direction = delta > 0.005 ? 'up' : delta < -0.005 ? 'down' : 'neutral';
  const value = (Math.abs(delta) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';
  return { value, direction };
}

/**
 * Retorna um objeto com { trend } somente se o trend for definido.
 * Compativel com exactOptionalPropertyTypes: true.
 */
function trendProps(
  trend: { value: string; direction: 'up' | 'down' | 'neutral' } | undefined,
): { trend: { value: string; direction: 'up' | 'down' | 'neutral' } } | Record<string, never> {
  if (trend === undefined) return {};
  return { trend };
}

function OverviewSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-surface-1 p-5"
          style={{ boxShadow: 'var(--elev-2)', minHeight: '100px' }}
        >
          <div
            className="mb-3 h-2.5 w-24 rounded-pill animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div
            className="h-8 w-16 rounded-xs animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div
            className="mt-2 h-2 w-32 rounded-pill animate-pulse"
            style={{ background: 'var(--border-subtle)' }}
          />
        </div>
      ))}
    </div>
  );
}

function OverviewError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-4 rounded-md border px-6 py-10 text-center"
      style={{ borderColor: 'var(--border)', background: 'var(--danger-bg)' }}
    >
      <p className="font-sans text-sm text-ink-2">Não foi possível carregar os dados.</p>
      <button
        type="button"
        onClick={onRetry}
        className="font-sans text-sm font-semibold rounded-sm px-4 py-2 transition-all duration-fast"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

function OverviewEmpty({ rangeLabel }: { rangeLabel?: string }): React.JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-md border border-dashed px-6 py-10 text-center"
      style={{ borderColor: 'var(--border-subtle)' }}
    >
      <p className="font-sans text-sm text-ink-3">
        Sem dados em {rangeLabel !== undefined ? rangeLabel.toLowerCase() : 'no período'} para o
        escopo selecionado.
      </p>
      <p className="font-sans text-xs text-ink-3">Tente ampliar o período ou ajustar o escopo.</p>
    </div>
  );
}

function OverviewCards({
  data,
  compareWithPrevious,
}: {
  data: OverviewResponse;
  compareWithPrevious: boolean;
}): React.JSX.Element {
  const prev = compareWithPrevious ? data.previousPeriod : undefined;
  // F52-S01: mesmo texto e mesma fórmula do dashboard (concluídos ÷ leads novos).
  const conversion = conversionDisplay(
    {
      concluded: data.leads.closedWon,
      base: data.leads.newInPeriod,
      rate: data.leads.conversionRate,
    },
    data.range.label,
  );
  const prevRate = prev?.leads.conversionRate ?? null;
  const isEmpty =
    data.leads.total === 0 &&
    data.simulations.total === 0 &&
    data.contracts.active === 0 &&
    data.conversations.open === 0 &&
    data.conversations.resolved === 0;

  if (isEmpty) return <OverviewEmpty rangeLabel={data.range.label} />;

  const totalContracts = data.contracts.active + data.contracts.defaulted;
  const adimplenciaRate = totalContracts > 0 ? data.contracts.active / totalContracts : 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Stat
        label="Leads no período"
        value={fmtNumber(data.leads.total)}
        description={'criados -- ' + data.range.label.toLowerCase()}
        {...trendProps(prev ? deltaTrend(data.leads.total, prev.leads.total) : undefined)}
      />
      <Stat
        label="Base de leads"
        value={fmtNumber(data.leads.totalAllTime)}
        description="total acumulado -- não depende do período"
      />
      <Stat
        label="Conversão"
        value={conversion.value}
        description={conversion.description}
        {...trendProps(
          data.leads.conversionRate !== null && prevRate !== null
            ? deltaTrend(data.leads.conversionRate, prevRate)
            : undefined,
        )}
      />
      <Stat
        label="Simulações"
        value={fmtNumber(data.simulations.total)}
        description={
          data.simulations.total > 0
            ? 'média ' + fmtCurrency(data.simulations.amountAvg)
            : data.range.label.toLowerCase()
        }
        {...trendProps(
          prev ? deltaTrend(data.simulations.total, prev.simulations.total) : undefined,
        )}
      />
      <Stat
        label="Contratos ativos"
        value={fmtNumber(data.contracts.active)}
        description={
          data.contracts.activePrincipalSum > 0
            ? fmtCurrency(data.contracts.activePrincipalSum) + ' em carteira'
            : 'sem carteira'
        }
      />
      <Stat
        label="Adimplência"
        value={totalContracts > 0 ? fmtPercentFromRatio(adimplenciaRate) : '--'}
        description={
          data.contracts.defaulted > 0
            ? fmtNumber(data.contracts.defaulted) + ' inadimplentes'
            : 'sem inadimplência'
        }
      />
      <Stat
        label="Conversas abertas"
        value={fmtNumber(data.conversations.open)}
        description="aguardando atendimento -- agora"
      />
      <Stat
        label="Resolvidas (total)"
        value={fmtNumber(data.conversations.resolved)}
        description="acumulado -- não depende do período"
      />
    </div>
  );
}

interface OverviewSectionProps {
  query: Partial<CommonReportQuery>;
}

/**
 * Secao Visao Geral. Consome /api/reports/overview com os filtros ativos.
 * Estados: loading (skeleton) / error / empty / success (8 KPI cards).
 */
export function OverviewSection({ query }: OverviewSectionProps): React.JSX.Element {
  const compareWithPrevious = query.compareWithPrevious ?? false;
  const { data, isLoading, isFetching, isError, isForbidden, updatedAt, refetch } =
    useReportsOverview(query);

  if (isForbidden) {
    return (
      <div
        className="rounded-md border px-6 py-8 text-center"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-1)' }}
      >
        <p className="font-sans text-sm text-ink-3">
          Você não tem permissão para visualizar a Visão Geral.
        </p>
      </div>
    );
  }
  if (isLoading) return <OverviewSkeleton />;
  if (isError) return <OverviewError onRetry={refetch} />;
  if (!data) return <OverviewEmpty />;

  // A serie ja vem diaria da MV; aqui so a adaptamos ao shape do grafico, que e
  // o mesmo do dashboard -- duas implementacoes divergiriam com o tempo.
  const chartSeries = data.series.map((point) => ({
    day: point.day,
    leads: point.leads,
    interactions: point.conversations,
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <FreshnessIndicator
          updatedAt={updatedAt}
          isFetching={isFetching}
          onRefresh={refetch}
          refreshedAt={data.refreshedAt}
          timeZone={data.range.timeZone}
        />
      </div>
      <OverviewCards data={data} compareWithPrevious={compareWithPrevious} />
      <DailySeriesChart
        data={chartSeries}
        rangeLabel={data.range.label}
        metricLabel="Conversas"
        unitLabel="conversas"
      />
    </div>
  );
}
